import { useEffect, useRef, useState } from 'react';
import { useSetDestinationEnabled } from '../../api/hooks';
import { ApiError } from '../../api/client';
import { darkTheme } from '../../theme/tokens';

const t = darkTheme;

/**
 * The destination kill switch (SC-3122). It changes what leaves the building, so
 * flipping it never writes directly — it opens an inline confirmation that says
 * what will happen, and only the confirm button sends the PATCH.
 *
 * The switch shows the SERVER's state throughout (the mutation isn't
 * optimistic), so a refused change can't leave it pointing the wrong way.
 *
 * Callers render this only for an `active` destination with `manage_destinations`:
 * a `deleting` one is one-way and gets no control at all.
 */
export function KillSwitch({
  id,
  name,
  enabled,
}: {
  id: string;
  name: string;
  enabled: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const mutation = useSetDestinationEnabled();
  const next = !enabled;
  const switchRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  // Only a close that follows an open hands focus back — not the first render.
  const wasConfirming = useRef(false);

  // The switch is disabled while the question is open, and a disabled element
  // drops focus to <body> — so a keyboard user would have to hunt for the answer.
  // Focus goes to Cancel (the safe default on a question about what leaves the
  // building), then back to the switch when the question closes either way.
  useEffect(() => {
    if (confirming) cancelRef.current?.focus();
    else if (wasConfirming.current) switchRef.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);

  const confirm = () => {
    mutation.mutate(
      { id, enabled: next },
      { onSuccess: () => setConfirming(false) },
    );
  };

  const cancel = () => {
    setConfirming(false);
    mutation.reset();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: t.text2 }}>
        Sync
        <button
          ref={switchRef}
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={`Sync to ${name}`}
          disabled={confirming}
          onClick={() => setConfirming(true)}
          style={{
            position: 'relative',
            width: 38,
            height: 22,
            borderRadius: 999,
            border: `1px solid ${enabled ? t.accentBorder : t.border2}`,
            background: enabled ? t.accent : t.surfaceDepressed,
            cursor: confirming ? 'default' : 'pointer',
            opacity: confirming ? 0.6 : 1,
            padding: 0,
          }}
        >
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 2,
              left: enabled ? 18 : 2,
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: enabled ? t.accentButtonText : t.text3,
              transition: 'left 120ms ease',
            }}
          />
        </button>
      </label>

      {confirming && (
        <div
          role="group"
          aria-label={next ? `Turn ${name} back on` : `Pause ${name}`}
          style={{
            width: 340,
            padding: '12px 14px',
            borderRadius: 8,
            border: `1px solid ${next ? t.accentBorder : t.amber}`,
            background: next ? t.accentBg : t.amberBgSubtle,
            fontSize: 13,
            color: t.text,
          }}
        >
          <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
            {next ? `Turn ${name} back on?` : `Pause ${name}?`}
          </p>
          <p style={{ margin: '0 0 12px', color: t.text2 }}>
            {next
              ? 'EKOM will resume syncing your data to it.'
              : 'EKOM will stop syncing your data to it until it’s switched back on. Product assignments and exports aren’t affected.'}
          </p>
          {mutation.isError && (
            <p role="alert" style={{ margin: '0 0 10px', color: t.redText }}>
              {errorCopy(mutation.error)}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              ref={cancelRef}
              type="button"
              onClick={cancel}
              disabled={mutation.isPending}
              style={buttonStyle('secondary')}
            >
              Cancel
            </button>
            <button type="button" onClick={confirm} disabled={mutation.isPending} style={buttonStyle(next ? 'primary' : 'warn')}>
              {mutation.isPending ? 'Saving…' : next ? 'Turn on' : 'Pause'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Nothing changed on a failure — the switch still shows the server's state — so
 *  the copy says so. A 422 is almost always "being removed"; the row refetches
 *  and the page swaps the switch for the removal banner. */
function errorCopy(error: Error): string {
  if (error instanceof ApiError) {
    if (error.status === 422) return 'This connection can’t be changed right now — it may be being removed.';
    if (error.status === 403) return 'You don’t have permission to change this connection.';
    if (error.status === 404) return 'This connection no longer exists.';
  }
  return 'Couldn’t save the change. Nothing was changed.';
}

function buttonStyle(kind: 'primary' | 'warn' | 'secondary') {
  const palette = {
    primary: { bg: t.accent, fg: t.accentButtonText, border: t.accent },
    warn: { bg: t.amber, fg: t.accentButtonText, border: t.amber },
    secondary: { bg: 'transparent', fg: t.text2, border: t.border2 },
  }[kind];
  return {
    padding: '5px 12px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    background: palette.bg,
    color: palette.fg,
    border: `1px solid ${palette.border}`,
  };
}
